import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, Select, DatePicker } from 'antd';
import { FaChartLine, FaShoppingCart, FaDollarSign, FaUser, FaFilter } from 'react-icons/fa';
import axios from 'axios';
import { base_url } from "../utils/baseUrl";
import { useNavigate } from "react-router-dom";
import TopSellingProducts from '../components/TopSellingProducts';
import MostSellingLocations from '../components/MostSellingLocations';

const { Option } = Select;
const { RangePicker } = DatePicker;

const Sales = () => {
  const [analytics, setAnalytics] = useState({
    total_orders: 0,
    total_sales: 0,
    average_order_value: 0,
    unique_customers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState("monthly");
  const [dateRange, setDateRange] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/");
      return;
    }
    fetchSalesAnalytics();
  }, [navigate, filterPeriod, dateRange]);

  const fetchSalesAnalytics = async () => {
    try {
      setLoading(true);
      let url = `${base_url}/api/sales/analytics`;
      const params = new URLSearchParams();

      // Always send period parameter to ensure backend knows the filter
      params.append("period", filterPeriod);

      if (dateRange && dateRange.length === 2) {
        try {
          params.append("startDate", dateRange[0].format("YYYY-MM-DD"));
          params.append("endDate", dateRange[1].format("YYYY-MM-DD"));
        } catch (error) {
          console.error("Date formatting error:", error);
          // Fallback to manual date formatting
          const startDate = dateRange[0];
          const endDate = dateRange[1];
          params.append("startDate", `${startDate.$y}-${String(startDate.$M + 1).padStart(2, '0')}-${String(startDate.$D).padStart(2, '0')}`);
          params.append("endDate", `${endDate.$y}-${String(endDate.$M + 1).padStart(2, '0')}-${String(endDate.$D).padStart(2, '0')}`);
        }
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      setAnalytics(response.data.analytics || {
        total_orders: 0,
        total_sales: 0,
        average_order_value: 0,
        unique_customers: 0
      });
      setError(null);
    } catch (err) {
      console.error("Error fetching sales analytics:", err);
      if (err.response?.status === 404) {
        setError("Sales analytics endpoint not found. Please ensure the backend server is running and the sales routes are deployed.");
      } else if (err.code === 'ERR_NETWORK') {
        setError("Unable to connect to the server. Please check your internet connection and try again.");
      } else {
        setError("Failed to fetch sales analytics. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (value) => {
    setFilterPeriod(value);
    if (value !== "custom") {
      setDateRange(null);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates) {
      setFilterPeriod("custom");
    }
  };

  const getFilterTitle = () => {
    if (filterPeriod === "weekly") return "Last 7 Days";
    if (filterPeriod === "monthly") return "Last 30 Days";
    if (filterPeriod === "yearly") return "Last 1 Year";
    if (filterPeriod === "custom" && dateRange) {
      return `${dateRange[0].format("MMM DD")} - ${dateRange[1].format("MMM DD, YYYY")}`;
    }
    return "All Time";
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading sales analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <button
            onClick={fetchSalesAnalytics}
            style={{
              background: "#6B3F4F",
              color: "white",
              border: "none",
              padding: "4px 8px",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h1 style={{ color: '#6B3F4F', margin: 0 }}>
            <FaChartLine style={{ marginRight: '10px' }} />
            Sales Analytics - {getFilterTitle()}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FaFilter style={{ color: "#6B3F4F" }} />
            <Select
              value={filterPeriod}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
              placeholder="Select Period"
            >
              <Option value="all">All Time</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
            {filterPeriod === "custom" && (
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
              />
            )}
            <button
              onClick={fetchSalesAnalytics}
              style={{
                background: "#6B3F4F",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              Refresh
            </button>
          </div>
        </div>
        <p style={{ color: '#666', fontSize: '16px' }}>
          Overview of your store's sales performance and top selling products
        </p>
      </div>

      {/* Sales Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={analytics.total_orders}
              prefix={<FaShoppingCart style={{ color: '#6B3F4F' }} />}
              valueStyle={{ color: '#6B3F4F' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Sales (₹)"
              value={analytics.total_sales}
              valueStyle={{ color: '#6B3F4F' }}
              formatter={(value) => {
                const numValue = parseFloat(value) || 0;
                return `₹${numValue.toLocaleString()}`;
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Average Order Value"
              value={analytics.average_order_value}
              valueStyle={{ color: '#6B3F4F' }}
              formatter={(value) => {
                const numValue = parseFloat(value) || 0;
                return `₹${numValue.toFixed(2)}`;
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Unique Customers"
              value={analytics.unique_customers}
              prefix={<FaUser style={{ color: '#6B3F4F' }} />}
              valueStyle={{ color: '#6B3F4F' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Top Selling Products and Locations */}
      <div style={{ marginTop: '30px' }}>
        <TopSellingProducts
          filterPeriod={filterPeriod}
          dateRange={dateRange}
        />
        <div style={{ marginTop: '20px' }}>
          <MostSellingLocations
            filterPeriod={filterPeriod}
            dateRange={dateRange}
          />
        </div>
      </div>
    </div>
  );
};

export default Sales; 